import { Link } from "react-router-dom";
import Title from "@/components/Title";
import { Trans, useTranslation } from "react-i18next";
import * as LINKS from "@/lib/links";

type InternalLink = {
  name: string;
  path: string;
  description: string;
};

type ExternalLink = {
  name: string;
  href: string;
  description: string;
};

type SiteLink = InternalLink | ExternalLink;

const Sitemap = () => {
  const { t } = useTranslation("sitemap");
  const currentYear = new Date().getFullYear();

  const siteStructure: Record<string, SiteLink[]> = {
    [t("categories.mainPages")]: [
      {
        name: t("links.home.name"),
        path: LINKS.ROOT,
        description: t("links.home.description"),
      },
      {
        name: t("links.pricing.name"),
        path: LINKS.PRICING,
        description: t("links.pricing.description"),
      },
    ],
    [t("categories.tools")]: [
      {
        name: t("links.communicationGenerator.name"),
        path: LINKS.WRITING_WIZARD,
        description: t("links.communicationGenerator.description"),
      },
      {
        name: t("links.lessonLab.name"),
        path: LINKS.LESSON_LAB,
        description: t("links.lessonLab.description"),
      },
      {
        name: t("links.researcher.name"),
        path: LINKS.RESEARCHER,
        description: t("links.researcher.description"),
      },
      {
        name: t("links.resourceCustomiser.name"),
        path: LINKS.RESOURCE_REFINERY,
        description: t("links.resourceCustomiser.description"),
      },
    ],
    [t("categories.accountSettings")]: [
      {
        name: t("links.signInRegister.name"),
        path: LINKS.AUTHENTICATE,
        description: t("links.signInRegister.description"),
      },
      {
        name: t("links.accountSettings.name"),
        path: LINKS.SETTINGS,
        description: t("links.accountSettings.description"),
      },
      {
        name: t("links.templates.name"),
        path: LINKS.SETTINGS_TEMPLATES,
        description: t("links.templates.description"),
      },
      {
        name: t("links.subscription.name"),
        path: LINKS.SETTINGS_SUBSCRIPTION,
        description: t("links.subscription.description"),
      },
      {
        name: t("links.invoices.name"),
        path: LINKS.SETTINGS_INVOICES,
        description: t("links.invoices.description"),
      },
      {
        name: t("links.changePassword.name"),
        path: LINKS.SETTINGS_CHANGE_PASSWORD,
        description: t("links.changePassword.description"),
      },
      {
        name: t("links.resetPassword.name"),
        path: LINKS.AUTHENTICATE_RESET_PASSWORD,
        description: t("links.resetPassword.description"),
      },
    ],
    [t("categories.legalSupport")]: [
      {
        name: t("links.privacyPolicy.name"),
        path: LINKS.PRIVACY_POLICY,
        description: t("links.privacyPolicy.description"),
      },
      {
        name: t("links.termsOfService.name"),
        path: LINKS.TERMS_OF_SERVICE,
        description: t("links.termsOfService.description"),
      },
      {
        name: t("links.sitemap.name"),
        path: LINKS.SITEMAP,
        description: t("links.sitemap.description"),
      },
    ],
    [t("categories.externalLinks")]: [
      {
        name: t("links.blog.name"),
        href: "https://whiteboard.tchr.tools",
        description: t("links.blog.description"),
      },
      {
        name: t("links.facebook.name"),
        href: "https://www.facebook.com/groups/722840903696499",
        description: t("links.facebook.description"),
      },
      {
        name: t("links.reddit.name"),
        href: "https://www.reddit.com/r/TeacherTools/",
        description: t("links.reddit.description"),
      },
      {
        name: t("links.linkedin.name"),
        href: "https://www.linkedin.com/groups/13323124/",
        description: t("links.linkedin.description"),
      },
      {
        name: t("links.x.name"),
        href: "https://x.com/tchrtools",
        description: t("links.x.description"),
      },
      {
        name: t("links.bluesky.name"),
        href: "https://bsky.app/profile/tchr.tools",
        description: t("links.bluesky.description"),
      },
      {
        name: t("links.emailSupport.name"),
        href: "mailto:hello@tchr.tools",
        description: t("links.emailSupport.description"),
      },
    ],
  };

  return (
    <main className="flex flex-col px-10 py-[30px] overflow-auto md:px-20 md:py-[24px] bg-very-light w-full min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Title title={t("title")}>{t("title")}</Title>
          <p className="mt-4 text-lg text-muted-foreground">{t("subtitle")}</p>
        </div>

        <div className="grid gap-8 md:grid-cols-1 lg:grid-cols-2">
          {Object.entries(siteStructure).map(([category, links], i) => (
            <div
              key={`${category}-${i}`}
              className="p-6 border rounded-lg bg-card border-border"
            >
              <h2 className="mb-4 text-xl font-semibold text-foreground">
                {category}
              </h2>
              <ul className="space-y-3">
                {links.map((link, i) => {
                  const isInternalLink = "path" in link;
                  const isExternalLink = "href" in link;

                  return (
                    <li
                      key={`${link.name}-${i}`}
                      className="pb-3 border-b border-border/50 last:border-b-0 last:pb-0"
                    >
                      {isInternalLink ? (
                        <Link to={link.path} className="block group">
                          <div className="font-medium transition-colors text-primary group-hover:text-primary/80">
                            {link.name}
                          </div>
                          <div className="mt-1 text-sm text-muted-foreground">
                            {link.description}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground/70">
                            {link.path}
                          </div>
                        </Link>
                      ) : isExternalLink ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block group"
                        >
                          <div className="flex items-center gap-2 font-medium transition-colors text-primary group-hover:text-primary/80">
                            {link.name}
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </div>
                          <div className="mt-1 text-sm text-muted-foreground">
                            {link.description}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground/70">
                            {link.href}
                          </div>
                        </a>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="p-6 mt-12 border rounded-lg bg-muted/50 border-border/50">
          <h2 className="mb-3 text-lg font-semibold text-foreground">
            {t("about.title")}
          </h2>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>{t("about.description")}</p>
            <Trans i18nKey="about.internalLinks" ns="sitemap" />
            <br />
            <Trans i18nKey="about.externalLinks" ns="sitemap" />

            <p className="pt-2 border-t border-border/50">
              {t("about.lastUpdated", {
                date: new Date().toLocaleDateString(),
                year: currentYear,
              })}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Sitemap;
